import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { deleteContractorContact } from '../../services/contractorsService';
import styles from './ManageContactsModal.module.css';

const ManageContactsModal = ({ contacts, onClose, onContactDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const [deletingContactId, setDeletingContactId] = useState(null);

  const handleRemoveContact = async (contactId, contactName) => {
    const confirmMessage = `⚠️ WARNING: This will permanently delete "${contactName}" and ALL related data:\n\n` +
      `• The associated user account\n` +
      `• The contractor contact record\n\n` +
      `This action cannot be undone. Are you sure you want to continue?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setDeleting(true);
      setDeletingContactId(contactId);

      const response = await deleteContractorContact(contactId);

      if (response.success) {
        toast.success(`"${contactName}" deleted successfully!`);
        
        // Call the callback to refresh contacts list
        if (onContactDeleted) {
          onContactDeleted(contactId);
        }
      } else {
        // Handle specific error cases
        if (response.error === 'CantRemoveWithExistingRelationException') {
          toast.error(
            `Cannot delete "${contactName}": This contact has ${response.ticketCount || 0} related ticket(s). Please remove or reassign tickets first.`
          );
        } else {
          toast.error(response.message || 'Failed to delete contact');
        }
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error(`Failed to delete contact: ${error.message || 'Unknown error'}`);
    } finally {
      setDeleting(false);
      setDeletingContactId(null);
    }
  };

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modal}>
        <div className={styles.modalHeader}>
          <button type="button" className={styles.closeBtn} onClick={onClose}>
            &times;
          </button>
          <h4 className={styles.modalTitle}>Manage contacts</h4>
        </div>
        
        <div className={styles.modalBody}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Job title</th>
                <th>Phone number</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {contacts.length > 0 ? (
                contacts.map((contact) => (
                  contact.user && (
                    <tr key={contact.id}>
                      <td>{`${contact.user.first_name} ${contact.user.last_name}`}</td>
                      <td>{contact.user.job_title || '-'}</td>
                      <td>
                        {contact.user.phone_number || contact.user.mobile_number || '-'}
                      </td>
                      <td>
                        <div className={styles.actionButtons}>
                          <Link 
                            to={`/users/${contact.user.id}/edit`} 
                            className={styles.btnInfo}
                          >
                            Edit
                          </Link>
                          <button 
                            className={styles.btnDanger}
                            onClick={() => handleRemoveContact(contact.id, `${contact.user.first_name} ${contact.user.last_name}`)}
                            disabled={deleting && deletingContactId === contact.id}
                          >
                            {deleting && deletingContactId === contact.id ? 'Removing...' : 'Remove'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                ))
              ) : (
                <tr>
                  <td colSpan="4" className={styles.noData}>
                    No contacts found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        <div className={styles.modalFooter}>
          <button type="button" className={styles.btnDefault} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ManageContactsModal; 